import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { productsApi } from '../api/productsApi'
import { useToast } from '../../../shared/components/toast/useToast'
import type { AttributeSchema } from '../types'

export const useAttributeSchemas = (solutionId: string, tenantId?: string) => {
  return useQuery({
    queryKey: ['attribute-schemas', solutionId, tenantId],
    queryFn: () => productsApi.platform.getAttributeSchemas(solutionId, tenantId),
    enabled: !!solutionId,
    staleTime: 10 * 60 * 1000,
  })
}

export const useCreateAttributeSchema = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: (data: Omit<AttributeSchema, 'id'>) =>
      productsApi.platform.createSchema(data as Omit<AttributeSchema, 'id' | 'variantValues'>),
    onSuccess: (schema) => {
      queryClient.invalidateQueries({ queryKey: ['attribute-schemas', schema.solutionId] })
      toast.success('Attribute schema created')
    },
    onError: () => toast.error('Failed to create attribute schema'),
  })
}

export const useUpdateAttributeSchema = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AttributeSchema> }) =>
      productsApi.platform.updateSchema(id, data),
    onSuccess: (schema) => {
      queryClient.invalidateQueries({ queryKey: ['attribute-schemas', schema.solutionId] })
      toast.success('Attribute schema updated')
    },
    onError: () => toast.error('Failed to update attribute schema'),
  })
}

export const useDeleteAttributeSchema = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ id, solutionId }: { id: string; solutionId: string }) =>
      productsApi.platform.deleteSchema(id).then(() => solutionId),
    onSuccess: (solutionId) => {
      queryClient.invalidateQueries({ queryKey: ['attribute-schemas', solutionId] })
      toast.success('Attribute schema deleted')
    },
    onError: () => toast.error('Failed to delete attribute schema'),
  })
}
